import { useCallback, useEffect, useRef, useState } from 'react';
import CarouselControls from './CarouselControls';
import { episodeCovers, watchUrl } from './episodeCovers';

const INTERVAL = 6000;

function EpisodeCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [inViewport, setInViewport] = useState(true);
  const [tabVisible, setTabVisible] = useState(!document.hidden);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [remaining, setRemaining] = useState(INTERVAL);
  const [activeImageLoaded, setActiveImageLoaded] = useState(false);
  const widgetRef = useRef(null);
  const touchStart = useRef(null);
  const deadline = useRef(null);
  const remainingRef = useRef(INTERVAL);
  const timer = useRef(null);
  const wasRunning = useRef(false);

  const count = episodeCovers.length;
  const running = playing && !reducedMotion && inViewport && tabVisible && !hovered && !focused && count > 1;

  const navigate = useCallback((index) => {
    if (!count) return;
    window.clearTimeout(timer.current);
    deadline.current = null;
    remainingRef.current = INTERVAL;
    setRemaining(INTERVAL);
    setCurrentIndex((index + count) % count);
  }, [count]);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setInViewport(entry.isIntersecting),
      { threshold: 0.15 }
    );
    if (widgetRef.current) observer.observe(widgetRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  useEffect(() => {
    if (count < 2) return;
    const image = new Image();
    image.src = episodeCovers[(currentIndex + 1) % count].image;
  }, [count, currentIndex]);

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (!running) {
      if (wasRunning.current && deadline.current !== null) {
        remainingRef.current = Math.max(0, deadline.current - performance.now());
        setRemaining(remainingRef.current);
      }
      deadline.current = null;
      wasRunning.current = false;
      return;
    }

    const duration = remainingRef.current;
    deadline.current = performance.now() + duration;
    wasRunning.current = true;
    timer.current = window.setTimeout(() => {
      deadline.current = null;
      remainingRef.current = INTERVAL;
      setRemaining(INTERVAL);
      setCurrentIndex((index) => (index + 1) % count);
    }, duration);

    return () => window.clearTimeout(timer.current);
  }, [running, currentIndex, count]);

  const onTouchEnd = (event) => {
    if (touchStart.current === null) return;
    const distance = event.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(distance) > 42) navigate(currentIndex + (distance < 0 ? 1 : -1));
  };

  return (
    <main className="territory-widget" ref={widgetRef}>
      <section
        className="carousel-shell"
        aria-roledescription="carousel"
        aria-label="Digital Cowboy Territory episode covers"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocusCapture={() => setFocused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
        }}
      >
        <div className="editorial-heading">
          <span className="registration-mark" aria-hidden="true" />
          <span>TRANSMITTING FROM THE TERRITORY</span>
          <span className="registration-mark" aria-hidden="true" />
        </div>

        <div
          className="cover-frame"
          onTouchStart={(event) => { touchStart.current = event.changedTouches[0].clientX; }}
          onTouchEnd={onTouchEnd}
          onTouchCancel={() => { touchStart.current = null; }}
        >
          {count ? episodeCovers.map((cover, index) => (
            <img
              key={`${cover.image}-${index}`}
              className={`cover-image ${index === currentIndex ? 'is-current' : ''}`}
              src={cover.image}
              alt={index === currentIndex ? cover.alt : ''}
              aria-hidden={index !== currentIndex}
              width="1920"
              height="1080"
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : 'auto'}
              onLoad={index === 0 ? () => setActiveImageLoaded(true) : undefined}
            />
          )) : (
            <div className="empty-cover" role="img" aria-label="Episode covers have not been added yet">
              Episode covers coming soon
            </div>
          )}
          {count > 1 && !reducedMotion && (
            <div className="progress-track" aria-hidden="true">
              <div
                className="progress-line"
                style={{
                  '--progress-duration': `${remaining}ms`,
                  '--progress-start': `${(1 - remaining / INTERVAL) * 100}%`
                }}
                data-running={running}
              />
            </div>
          )}
        </div>

        <CarouselControls
          currentIndex={currentIndex}
          isPlaying={playing && !reducedMotion}
          onPrevious={() => navigate(currentIndex - 1)}
          onNext={() => navigate(currentIndex + 1)}
          onToggle={() => setPlaying((value) => !value)}
          onSelect={navigate}
          total={count}
          disabled={count < 2}
          reducedMotion={reducedMotion}
        />
        <p className="signature">Your Words Build Worlds.</p>
        <a className="watch-button" href={watchUrl} target="_blank" rel="noopener noreferrer">
          Watch Now
        </a>
      </section>
    </main>
  );
}

export default EpisodeCarousel;